import React, { useState, useEffect } from "react";
import { fetchPrompts } from "../Utils/PromptUtils";
import { useSelector } from "react-redux";
import { selectAccessToken, selectAuthenticated } from "../selectors";
import { Toast } from 'flowbite-react';
import { useNavigate } from "react-router-dom";
import SignInPrompt from "./SignInPrompt";
import { XIcon } from "lucide-react";

const PromptGallery = ({ setShowPromptGalleryModal, onSelectPrompt }) => {
  const [prompts, setPrompts] = useState([]);
  const [promptsFetched, setPromptsFetched] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedType, setSelectedType] = useState("all");
  const [categories, setCategories] = useState([]);
  const [types, setTypes] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const accessToken = useSelector(selectAccessToken);
  const authenticated = useSelector(selectAuthenticated);
  const navigate = useNavigate();

  useEffect(() => {
    // Only fetch prompts if user is authenticated (explicitly true)
    if (authenticated === true) {
      const getPrompts = async () => {
        const response = await fetchPrompts(accessToken);
        if (response.status === "success") {
          setPrompts(response.data);
          // Extract unique categories and types
          const uniqueCategories = [...new Set(response.data.map(prompt => prompt.category))];
          const uniqueTypes = [...new Set(response.data.map(prompt => prompt.type))];
          setCategories(uniqueCategories);
          setTypes(uniqueTypes);
          setPromptsFetched(true);
        }else{
          console.log("Failed to fetch prompts", response.message)
        }
      };
      getPrompts();
    } else {
      // Reset promptsFetched when not authenticated to prevent loading state
      setPromptsFetched(false);
    }
  }, [authenticated, accessToken]);

  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === "Escape") {
        setShowPromptGalleryModal(false);
      }
    };

    document.addEventListener("keydown", handleEsc);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [setShowPromptGalleryModal]);

  const filteredPrompts = prompts.filter(prompt => {
    let stringifiedPrompt = JSON.stringify(prompt);
    const matchesSearch = stringifiedPrompt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "all" || prompt.category === selectedCategory;
    const matchesType = selectedType === "all" || prompt.type === selectedType;
    return matchesSearch && matchesCategory && matchesType;
  });

  const handlePromptSelect = (prompt) => {
    setSelectedPrompt(prompt);
    if (onSelectPrompt) {
      onSelectPrompt(prompt);
    }
    setShowPromptGalleryModal(false);
  };

  const handleCopyPrompt = (prompt) => {
    navigator.clipboard.writeText(prompt);
    setToastMessage("Prompt copied to clipboard!");
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4"
      onClick={() => setShowPromptGalleryModal(false)}
    >
      
      {showToast && (
        <div className="fixed top-3 sm:top-4 left-1/2 transform -translate-x-1/2 z-[60] max-w-[90%] sm:max-w-xs">
          <Toast>
            <div className="inline-flex h-7 w-7 sm:h-8 sm:w-8 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-primary-500">
              <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
              </svg>
            </div>
            <div className="ml-2 sm:ml-3 text-xs sm:text-sm font-normal text-gray-700">
              {toastMessage}
            </div>
            <Toast.Toggle onDismiss={() => setShowToast(false)} />
          </Toast>
        </div>
      )}
      
      <div className={`relative w-full max-w-5xl ${authenticated === true ? 'max-h-full h-auto' : 'h-[400px]'} bg-gradient-to-br from-rose-50 via-white to-rose-100 rounded-xl sm:rounded-2xl shadow-2xl my-auto flex flex-col overflow-hidden`} onClick={(e) => e.stopPropagation()}>
        {/* Close Button - Fixed at top right */}
        <button
          type="button"
          onClick={() => setShowPromptGalleryModal(false)}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 p-2 text-gray-500 hover:text-gray-700 hover:bg-white/80 rounded-lg transition-all duration-200 flex-shrink-0 bg-white/60 shadow-sm"
          aria-label="Close modal"
          title="Close"
        >
          <XIcon className="h-5 w-5 sm:h-6 sm:w-6" />
        </button>

        {/* Header */}
        <div className="p-4 sm:p-5 md:p-6 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900">Prompt Gallery</h2>
          
          {/* Show search and filters only if authenticated */}
          {authenticated && (
          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            {/* Search Bar */}
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-2.5 sm:pl-3 flex items-center pointer-events-none">
                <svg
                  className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400 group-focus-within:text-primary-500 transition-colors"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search prompts by text, category, or type..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 sm:pl-10 pr-3 sm:pr-4 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg sm:rounded-xl bg-white focus:bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 placeholder-gray-400 text-gray-900 font-medium text-xs sm:text-sm md:text-base"
              />
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              {/* Category Filter */}
              <div className="flex-1">
                <label htmlFor="category" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <div className="relative">
                  <select
                    id="category"
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="w-full pl-3 sm:pl-4 pr-8 sm:pr-10 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg sm:rounded-xl bg-gray-50/50 focus:bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 appearance-none cursor-pointer text-gray-700 text-xs sm:text-sm"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1.5 sm:px-2 pointer-events-none">
                    <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>

              {/* Type Filter */}
              <div className="flex-1">
                <label htmlFor="type" className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <div className="relative">
                  <select
                    id="type"
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value)}
                    className="w-full pl-3 sm:pl-4 pr-8 sm:pr-10 py-2 sm:py-2.5 md:py-3 border border-gray-200 rounded-lg sm:rounded-xl bg-gray-50/50 focus:bg-white focus:ring-1 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 appearance-none cursor-pointer text-gray-700 text-xs sm:text-sm"
                  >
                    <option value="all">All Types</option>
                    {types.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  <div className="absolute inset-y-0 right-0 flex items-center px-1.5 sm:px-2 pointer-events-none">
                    <svg className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
          )}
        </div>

        {/* Content Area - Show AuthenticationRequired if not authenticated, otherwise show prompts */}
        <div className={`flex-1 min-h-0 ${authenticated === true ? 'overflow-y-auto' : 'overflow-hidden flex items-center justify-center'}`}>
        {authenticated !== true ? (
          <div className="flex-1 flex items-center justify-center p-4">
            <SignInPrompt
              title="Sign in to see prompts"
              message="Saved prompts will show up here."
              onSignIn={(e) => {
                if (e) {
                  e.preventDefault();
                  e.stopPropagation();
                }
                setShowPromptGalleryModal(false);
                setTimeout(() => {
                  navigate("/signin");
                }, 100);
              }}
              className="px-6 py-8"
            />
          </div>
        ) : (
        <div className="w-full mx-auto px-4 sm:px-5 md:px-6 py-4 sm:py-5 md:py-6">
          {promptsFetched ? (
            filteredPrompts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
                {filteredPrompts.map((prompt) => (
                  <div
                    key={prompt.id}
                    className="bg-white border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2 sm:mb-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1.5 sm:mb-2 truncate">
                          {prompt.category}
                        </h3>
                        <span className="inline-block px-2 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium text-primary-700 bg-primary-100 rounded-full">
                          {prompt.type}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {Array.isArray(prompt.prompts) ? (
                        prompt.prompts.map((singlePrompt, index) => (
                          <div
                            key={index}
                            className="p-2.5 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                            onClick={() => handlePromptSelect(singlePrompt)}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <p className="text-gray-700 text-xs sm:text-sm leading-relaxed flex-1 break-words">{singlePrompt}</p>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyPrompt(singlePrompt);
                                }}
                                className="text-gray-400 hover:text-primary-600 transition-colors ml-1.5 sm:ml-2 flex-shrink-0 p-1"
                                aria-label="Copy prompt"
                              >
                                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div
                          className="p-2.5 sm:p-3 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer transition-colors"
                          onClick={() => handlePromptSelect(prompt.prompts)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-gray-700 text-xs sm:text-sm leading-relaxed flex-1 break-words">{prompt.prompts}</p>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyPrompt(prompt.prompts);
                              }}
                              className="text-gray-400 hover:text-primary-600 transition-colors ml-1.5 sm:ml-2 flex-shrink-0 p-1"
                              aria-label="Copy prompt"
                            >
                              <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full min-h-[300px] sm:min-h-[400px] text-center px-4">
                <div className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-3 sm:mb-4">
                  <svg className="w-full h-full" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <p className="text-gray-500 text-base sm:text-lg font-medium">No prompts found</p>
                <p className="text-gray-400 text-xs sm:text-sm mt-1.5 sm:mt-2 px-2">Try adjusting your search or filters</p>
              </div>
            )
          ) : (
            <div className="flex items-center justify-center h-full min-h-[300px] sm:min-h-[400px]">
              <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-primary-600"></div>
            </div>
          )}
        </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default PromptGallery;










